import { mapAsync, partition, uniq } from 'es-toolkit';

import { ReadFile } from '@paragon/client-tools';
import {
  AnalysisSettings,
  ComponentDesign,
  DEFAULT_ORGANIZATION_SETTINGS,
  JackConnectionsOnGirderWithNames,
  JackConnectionWithNames,
  JackGirderConnection,
  JackInformation,
  LoadSettings,
  LumberList,
  MaterialSelection,
  MaterialSettingsPair,
  MemberAnalysisInputs,
  TrussSettings,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLumberLists } from '@paragon/material-lists';
import { addDialog, enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage, runWithSpinner } from '@paragon/task-spinner';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { SaveAction } from '../../components/interface/subpanels/settings/SettingsSaveButton';
import { getCommittedComponentDesigns } from '../selectors/getComponentDesignsSelectors';
import { getOrganizationForOpenContext } from '../selectors/getOrganizationForInsertingComponent';
import { startInsertionComponentDownload, switchInsertionComponentDesignType } from '../slices/applicationSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { addComponentDesignsToProject } from './appActions';
import { cancelInsertComponentDesign } from './insertActions';
import {
  adjustFeaturePatternTrussSettingsForInsertion,
  getLoadedStockLengthPricesForMaterialSettings,
  getShapeFromDefinitionType,
  unloadCurrentInsertionComponentDesign,
  updateInsertingTrussFromSettings,
} from './insertComponentDesignActions';
import {
  fetchAndLoadJackGirderConnections,
  MAX_CONCURRENT_COMPONENT_DESIGN_LOADS,
  receiveComponentDesign,
} from './loadActions';
import { cleanProfileTrussSettings } from './profileActions';
import { selectItem } from './selectActions';
import { loadOrganizationSettingsIfNeeded, saveAnalysisSettings, saveLoadSettings } from './settingsActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

interface ParsedTrussSettings {
  readonly settings: readonly TrussSettings[];
  readonly errors: readonly string[];
}

const getTrussSettings =
  (readFiles: readonly ReadFile[]): AsyncThunkActionReturn<ParsedTrussSettings> =>
  async (_, getState) => {
    const state = getState();

    const files = readFiles.map((readFile) => ({
      contents: readFile.contents,
      filename: `${readFile.filename}.${readFile.filetype}`,
    }));

    // TODO: kill?
    if (readFiles.some((file) => file.filetype === 'rvt')) {
      if (state.application.insertingObjectInfo?.type !== 'componentDesign') {
        throw new Error('Invalid insertion state.');
      }

      switch (state.application.insertingObjectInfo.definitionType) {
        case 'FeaturePattern':
          const featurePatternSettings = await designService.revit.extractFeaturePatternSettings(files);
          return { settings: featurePatternSettings, errors: [] };

        case 'Profile':
          const profileSettings = await designService.revit.extractProfileSettings(files);
          return { settings: profileSettings, errors: [] };

        case 'Wall':
          throw new Error('Insertion of wall .rvt files is currently not supported.');

        default:
          assertExhaustiveSwitch(state.application.insertingObjectInfo.definitionType);
      }
    }

    const parseTrussSettingsResponse = await designService.componentDesigns.postParseTrussSettings(files);
    return {
      settings: [
        ...parseTrussSettingsResponse.profileTrussSettings,
        ...parseTrussSettingsResponse.featurePatternTrussSettings,
      ],
      errors: parseTrussSettingsResponse.errors,
    };
  };

export const selectInsertionFiles =
  (files: readonly ReadFile[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Uploading file...'));

    const downloadId = {};
    dispatch(startInsertionComponentDownload(downloadId));

    try {
      const [girderConnectionsFiles, componentFiles] = partition(files, (file) => file.filetype === 'ldg');

      if (files.some((file) => file.filetype === 'rvt') && files.some((file) => file.filetype !== 'rvt')) {
        dispatch(enqueueError('Cannot upload RVT files along with other file types.'));
        return;
      }

      if (
        componentFiles
          .filter((file) => file.filetype === 'trs')
          .some(
            (trsFile) =>
              !girderConnectionsFiles.some(
                (girderConnectionsFile) => girderConnectionsFile.filename === trsFile.filename,
              ),
          )
      ) {
        dispatch(enqueueWarning('A TRS file was uploaded with no matching LDG file.'));
      }

      const { settings, errors } = await dispatch(getTrussSettings(componentFiles));
      for (const error of errors) {
        dispatch(enqueueError(error));
      }
      const { insertingObjectInfo } = getState().application;
      if (
        !insertingObjectInfo ||
        insertingObjectInfo.type !== 'componentDesign' ||
        !insertingObjectInfo.loadingInfo.isLoading ||
        insertingObjectInfo.loadingInfo.downloadId !== downloadId
      ) {
        return;
      }

      if (settings.length > 1 || girderConnectionsFiles.length > 0) {
        const girderConnectionsFilesForUpload = girderConnectionsFiles.map((file) => ({
          contents: file.contents,
          filename: `${file.filename}.${file.filetype}`,
        }));
        const jackConnectionsOnGirders = await designService.componentDesigns.postParseJackConnectionsOnGirders(
          girderConnectionsFilesForUpload,
        );
        for (const connection of jackConnectionsOnGirders) {
          if (isCycleInConnections(connection, jackConnectionsOnGirders)) {
            dispatch(
              enqueueError(
                `Circular carried component relationships are not allowed. Check girder ${connection.girderName}.`,
              ),
            );
            return;
          }
        }
        const committedNames = getCommittedComponentDesigns(getState()).map(
          (componentDesign) => componentDesign.component.name,
        );
        const namesBeingInserted = settings.map((item) => item.name);
        const namesInJackGirderConnections = jackConnectionsOnGirders
          .map((jackConnectionsOnGirder) => jackConnectionsOnGirder.girderName)
          .concat(
            jackConnectionsOnGirders.flatMap((jackConnectionsOnGirder) =>
              jackConnectionsOnGirder.connections.map((connection) => connection.jackName),
            ),
          );
        const allComponentDesignNames = committedNames.concat(namesBeingInserted);
        const unresolvableTrussNames = namesInJackGirderConnections.filter(
          (trussName) => !allComponentDesignNames.some((componentDesignName) => componentDesignName === trussName),
        );
        if (unresolvableTrussNames.length > 0) {
          dispatch(
            enqueueError(
              `The uploaded LDG file(s) references trusses that cannot be found: ${uniq(unresolvableTrussNames).join(
                ', ',
              )}`,
            ),
          );
          return;
        }
        const namesInvolvedInUpload = namesBeingInserted.concat(namesInJackGirderConnections);
        const duplicateNames = namesInvolvedInUpload.filter(
          (name1) => allComponentDesignNames.filter((name2) => name1 === name2).length > 1,
        );
        if (duplicateNames.length > 0) {
          dispatch(
            enqueueError(
              `The uploaded file(s) references truss names that are duplicated: ${uniq(duplicateNames).join(', ')}`,
            ),
          );
          return;
        }

        // Get insertion properties now because it'll be cleared by cancelInsertComponentDesign
        const {
          materialSelection,
          materialSettings,
          parentGuid,
          loadSettings,
          analysisSettings,
          jackConnections,
          jackInformation,
        } = insertingObjectInfo;

        dispatch(cancelInsertComponentDesign());

        await dispatch(
          insertComponentsFromPositionedSettings(
            settings,
            jackConnectionsOnGirders,
            parentGuid,
            materialSelection,
            materialSettings,
            loadSettings,
            jackConnections,
            jackInformation,
            analysisSettings,
          ),
        );

        return;
      }

      const singleInsertionSettings = settings[0];
      let adjustedInsertionSettings: TrussSettings;
      switch (singleInsertionSettings.definitionType) {
        case 'FeaturePattern':
          adjustedInsertionSettings = adjustFeaturePatternTrussSettingsForInsertion(
            singleInsertionSettings,
            singleInsertionSettings.name,
          );
          break;
        case 'Profile':
          adjustedInsertionSettings = cleanProfileTrussSettings(singleInsertionSettings);
          break;
        default:
          assertExhaustiveSwitch(singleInsertionSettings);
      }

      // We need to call this before `application.insertingObjectInfo.loadedInfo` gets cleared by
      // `switchInsertionComponentDesignType`
      dispatch(unloadCurrentInsertionComponentDesign());

      dispatch(
        switchInsertionComponentDesignType({
          componentType: adjustedInsertionSettings.componentType,
          definitionType: adjustedInsertionSettings.definitionType,
          templateType: 'default',
          shape: getShapeFromDefinitionType(adjustedInsertionSettings.definitionType),
          shapeDetails: null,
          designSettings: adjustedInsertionSettings,
        }),
      );

      await dispatch(updateInsertingTrussFromSettings({ preview: true, focusCameraOnTruss: true }));

      dispatch(
        addDialog(
          `We did our best to match the settings from your file. Please verify the settings are correct before continuing.`,
        ),
      );
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error while uploading files.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export function isCycleInConnections(
  connection: JackConnectionsOnGirderWithNames,
  connections: readonly JackConnectionsOnGirderWithNames[],
) {
  let childrenConnections = getChildrenConnections(connection.connections, connections);
  while (childrenConnections.length !== 0) {
    if (childrenConnections.some((childConnection) => childConnection.girderName === connection.girderName)) {
      return true;
    }
    const nextLevel = childrenConnections.flatMap((childConnection) => childConnection.connections);
    childrenConnections = getChildrenConnections(nextLevel, connections);
  }

  return false;
}

function getChildrenConnections(
  jackConnectionWithNames: readonly JackConnectionWithNames[],
  connections: readonly JackConnectionsOnGirderWithNames[],
) {
  return jackConnectionWithNames
    .map((jackConnection) => connections.find((c) => c.girderName === jackConnection.jackName))
    .filter(notNullish);
}

const createComponentDesignFromInsertionSettings =
  (
    insertionSettings: TrussSettings,
    materialSettings: MaterialSettingsPair,
    materialSelection: MaterialSelection,
    lumberLists: { readonly [guid: string]: LumberList },
    loadSettings: LoadSettings,
    jackConnections: readonly JackGirderConnection[],
    jackInformation: { readonly [componentDesignGuid: string]: JackInformation },
    analysisSettings: AnalysisSettings,
  ): AsyncThunkActionReturn<ComponentDesign> =>
  async (dispatch, getState) => {
    let createdComponentDesign;

    const organization = getOrganizationForOpenContext(getState());
    const lumberList = lumberLists[materialSelection.lumberListGuid];
    const { openContext } = getState().application;
    if (insertionSettings.definitionType === 'FeaturePattern') {
      const featurePatternMaterialSettings = materialSettings.featurePatternMaterialSettings;
      if (!featurePatternMaterialSettings) throw new Error('Expected material settings to be set.');
      const stockLengthPrices = getLoadedStockLengthPricesForMaterialSettings(
        getState(),
        featurePatternMaterialSettings,
      );

      const memberAnalysisInputs: MemberAnalysisInputs = {
        lumberList,
        lumberStockLengthPrices: stockLengthPrices,
        loadSettings: loadSettings,
        jackConnections: jackConnections,
        jackInformation: jackInformation,
        analysisSettings: analysisSettings,
        additionalLoads: null,
      };

      const { componentDesign: featurePatternTruss, timestamps } =
        await designService.componentDesigns.postCreateAndInsertFeaturePatternTruss(
          {
            featurePatternTrussSettings: adjustFeaturePatternTrussSettingsForInsertion(
              insertionSettings,
              insertionSettings.name,
            ),
            materialSettings: featurePatternMaterialSettings,
            organization,
            materialSelection,
            memberAnalysisInputs,
          },
          openContext?.type === 'project' ? openContext.projectGuid : undefined,
        );
      createdComponentDesign = featurePatternTruss;
      dispatch(setComponentDesignTimestamps({ timestamps }));
    } else if (insertionSettings.definitionType === 'Profile') {
      const profileMaterialSettings = materialSettings.profileMaterialSettings;
      if (!profileMaterialSettings) throw new Error('Expected material settings to be set.');
      const stockLengthPrices = getLoadedStockLengthPricesForMaterialSettings(getState(), profileMaterialSettings);

      const memberAnalysisInputs: MemberAnalysisInputs = {
        lumberList,
        lumberStockLengthPrices: stockLengthPrices,
        loadSettings: loadSettings,
        jackConnections: jackConnections,
        jackInformation: jackInformation,
        analysisSettings: analysisSettings,
        additionalLoads: null,
      };

      const organizationSettings = await dispatch(loadOrganizationSettingsIfNeeded());
      const { scarfCutThreshold } = organizationSettings ?? DEFAULT_ORGANIZATION_SETTINGS;

      const { componentDesign: profileTruss, timestamps } =
        await designService.componentDesigns.postCreateAndInsertProfileTruss(
          {
            profileTrussSettings: insertionSettings,
            materialSettings: profileMaterialSettings,
            scarfCutThreshold,
            organization,
            materialSelection,
            memberAnalysisInputs,
            webbingInfo: null,
          },
          openContext?.type === 'project' ? openContext.projectGuid : undefined,
        );
      createdComponentDesign = profileTruss;
      dispatch(setComponentDesignTimestamps({ timestamps }));
    } else {
      assertExhaustiveSwitch(insertionSettings);
    }

    return createdComponentDesign;
  };

const insertComponentsFromPositionedSettings =
  (
    positionedSettings: readonly TrussSettings[],
    jackConnectionsOnGirders: readonly JackConnectionsOnGirderWithNames[],
    parentGuid: string | null,
    materialSelection: MaterialSelection,
    materialSettings: MaterialSettingsPair,
    loadSettings: LoadSettings,
    jackConnections: readonly JackGirderConnection[],
    jackInformation: { readonly [componentDesignGuid: string]: JackInformation },
    analysisSettings: AnalysisSettings,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Inserting multiple components...'));

    try {
      const lumberLists = getLumberLists(getState());
      if (!lumberLists) {
        dispatch(enqueueError('Failed to insert components. Material lists not loaded.'));
        return;
      }

      const createdComponentDesigns = (
        await Promise.all(
          positionedSettings.map(async (settings: TrussSettings) => {
            try {
              return await dispatch(
                createComponentDesignFromInsertionSettings(
                  settings,
                  materialSettings,
                  materialSelection,
                  lumberLists,
                  loadSettings,
                  jackConnections,
                  jackInformation,
                  analysisSettings,
                ),
              );
            } catch (exception) {
              console.error(exception);
              dispatch(enqueueError(`Error while creating ${settings.name}`));
            }
          }),
        )
      ).filter(notNullish);

      const allComponentDesigns = getCommittedComponentDesigns(getState()).concat(createdComponentDesigns);

      await designService.componentDesigns.postJackGirderConnections(
        jackConnectionsOnGirders.map((jackGirderConnection) => ({
          girderGuid: allComponentDesigns.find(
            (componentDesign) => componentDesign.component.name === jackGirderConnection.girderName,
          )!.guid,
          connections: jackGirderConnection.connections.map((connection) => ({
            jackGuid: allComponentDesigns.find(
              (componentDesign) => componentDesign.component.name === connection.jackName,
            )!.guid,
            girderXLocation: connection.girderXLocation,
            girderChordType: connection.girderChordType,
            jackXLocation: connection.jackXLocation,
            jackChordType: connection.jackChordType,
            minimumAngleBetweenGirderAndJack: connection.minimumAngleBetweenGirderAndJack,
          })),
        })),
      );

      const processComponentDesign = async (componentDesign: ComponentDesign, index: number) => {
        try {
          await dispatch(
            receiveComponentDesign(componentDesign, {
              commitComponentDesign: true,
              replaceWorkingChanges: true,
              isPersisted: true,
              // Load jack-girder connections later so that we know we've loaded all the components before the
              // jack-girder connections
              skipJackGirderConnections: true,
              skipSettings: true, // Has not been added to project yet
            }),
          );

          if (index === 0) {
            dispatch(selectItem(componentDesign, 'componentDesign', { retainNotifications: true }));
          }
        } catch (exception) {
          console.error(exception);
          dispatch(enqueueError(`Error while inserting ${componentDesign.component.name}`));
        }
      };

      await mapAsync(createdComponentDesigns, (design, index) => processComponentDesign(design, index), {
        concurrency: MAX_CONCURRENT_COMPONENT_DESIGN_LOADS,
      });

      // Once all components have been inserted, add them to the project.
      // This will inherit missing settings, so it is necessary before `runParagonProcess`.
      const processedComponentDesignGuids = createdComponentDesigns.map((design) => design.guid);

      if (parentGuid != null) {
        await dispatch(addComponentDesignsToProject(processedComponentDesignGuids, parentGuid));
      } else {
        await dispatch(
          runWithSpinner('Saving component settings...', async () => {
            const analysisSettings = await designService.defaults.getDefaultAnalysisSettings();
            const loadSettings = await designService.defaults.getDefaultLoadSettings();

            await Promise.all(
              processedComponentDesignGuids.map(async (componentDesignGuid) => {
                await dispatch(
                  saveLoadSettings(loadSettings, {
                    action: SaveAction.ActualForSingleComponent,
                    modelGuid: componentDesignGuid,
                  }),
                );

                await dispatch(
                  saveAnalysisSettings(analysisSettings, {
                    action: SaveAction.ActualForSingleComponent,
                    modelGuid: componentDesignGuid,
                  }),
                );
              }),
            );
          }),
        );
      }

      await Promise.all(processedComponentDesignGuids.map((guid) => dispatch(fetchAndLoadJackGirderConnections(guid))));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
